import { useCallback, useEffect, useState } from "react";

const KEY = "littleloom.recentlyViewed";
const MAX_ITEMS = 8;

function load(): number[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === "number") : [];
  } catch {
    return [];
  }
}

function save(ids: number[]) {
  try {
    localStorage.setItem(KEY, JSON.stringify(ids));
  } catch {
    // ignore quota errors
  }
}

/**
 * Tracks recently viewed product IDs in localStorage (max 8, most recent first).
 *
 * Usage:
 *   const { recent, track } = useRecentlyViewed();
 *   useEffect(() => { if (product) track(product.id); }, [product?.id]);
 */
export function useRecentlyViewed() {
  const [recent, setRecent] = useState<number[]>(() => load());

  // Cross-tab sync
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === KEY) {
        setRecent(load());
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const track = useCallback((productId: number) => {
    setRecent((prev) => {
      const filtered = prev.filter((id) => id !== productId);
      const next = [productId, ...filtered].slice(0, MAX_ITEMS);
      save(next);
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    save([]);
    setRecent([]);
  }, []);

  return { recent, track, clear };
}